<script setup>
import { ref, computed } from 'vue'
import axios from '../axios'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'player-added'])

// Form data
const formData = ref({
  player_name: '',
  p_player_id: ''
})

// Form validation
const isSubmitting = ref(false)
const showNotification = ref(false)
const notificationMessage = ref('')

// Computed properties
const isFormValid = computed(() => {
  return formData.value.player_name.trim().length > 0
})

// Methods
const resetForm = () => {
  formData.value = {
    player_name: '',
    p_player_id: ''
  }
}

const closeModal = () => {
  resetForm()
  emit('close')
}

const handleSubmit = async () => {
  console.log('handleSubmit called', formData.value)
  
  if (!isFormValid.value) {
    console.log('Form is not valid, returning early')
    return
  }

  console.log('Starting API call...')
  isSubmitting.value = true

  try {
    const response = await axios.post('/api/v2/player', {
      name: formData.value.player_name.trim(),
      p_player_id: formData.value.p_player_id.trim() || null
    })

    emit('player-added', response.data)
    closeModal()
    
    // Show success notification
    showSuccessNotification('Player created successfully!')
    
    console.log('Player created successfully:', response.data)
  } catch (error) {
    console.error('Error creating player:', error)
    alert('Error creating player. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const handleInput = (field, value) => {
  formData.value[field] = value
}

// Notification functions
const showSuccessNotification = (message) => {
  notificationMessage.value = message
  showNotification.value = true
  
  // Auto hide after 3 seconds
  setTimeout(() => {
    showNotification.value = false
  }, 3000)
}

// Close modal on escape key
const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    closeModal()
  }
}
</script>

<template>
  <!-- Success Notification -->
  <Teleport to="body">
    <Transition name="notification">
      <div 
        v-if="showNotification"
        class="fixed top-4 right-4 z-[60] bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
      >
        <i class="fas fa-check-circle"></i>
        <span>{{ notificationMessage }}</span>
        <button 
          @click="showNotification = false"
          class="ml-2 text-white hover:text-gray-200"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="modal">
      <div 
        v-if="visible"
        class="fixed inset-0 z-50 flex items-center justify-center"
        @keydown="handleKeydown"
        tabindex="0"
      >
        <!-- Backdrop -->
        <div 
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="closeModal"
        ></div>
        
        <!-- Modal Content -->
        <div 
          class="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 class="text-xl font-semibold text-gray-900">Add New Player</h2>
            <button 
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
          
          <!-- Form -->
          <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
            <!-- Player Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Player Name *
              </label>
              <input 
                type="text"
                :value="formData.player_name"
                @input="handleInput('player_name', $event.target.value)"
                placeholder="Enter player name"
                class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                autofocus
              >
            </div>

            <!-- Player ID -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Player ID
              </label>
              <input 
                type="text"
                :value="formData.p_player_id"
                @input="handleInput('p_player_id', $event.target.value)"
                placeholder="Enter player ID (optional)"
                class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
            </div>

            <!-- Action Buttons -->
            <div class="flex space-x-3 pt-4">
              <button 
                type="submit"
                :disabled="!formData.player_name.trim() || isSubmitting"
                class="flex-1 bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
                {{ isSubmitting ? 'Creating...' : 'Create Player' }}
              </button>
              <button 
                type="button"
                @click="closeModal"
                :disabled="isSubmitting"
                class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.9) translateY(-20px);
}

.modal-enter-to .relative,
.modal-leave-from .relative {
  transform: scale(1) translateY(0);
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
